/**
 * battery-selector.js
 *
 * Wires interactivity for the battery selector on PDP.
 * - Mutually exclusive radio-style selection among .battery-card elements.
 * - Sets data-battery-* on .battery-cards for buy-button.js and
 *   product-bundle-sync.js to consume (no dependency on [data-product-bundle]).
 * - Dispatches battery:change event so product-bundle-sync.js can recompute totals.
 * - Keyboard: Enter/Space to select the focused card.
 */
(() => {
  const container = document.querySelector('.battery-cards');
  if (!container) return;

  const cards = Array.from(container.querySelectorAll('.battery-card'));
  if (cards.length < 2) return; // single-card (is_new / replaced) or empty — nothing to wire

  // ===== Helpers =====

  function isStandard(card) {
    return card.dataset.batteryTier === 'standard';
  }

  function selectCard(card) {
    cards.forEach((c) => {
      c.classList.remove('battery-card--active');
      c.setAttribute('aria-checked', 'false');
      c.setAttribute('tabindex', '-1');
    });

    card.classList.add('battery-card--active');
    card.setAttribute('aria-checked', 'true');
    card.setAttribute('tabindex', '0');

    if (isStandard(card)) {
      // Standard selected — clear battery upgrade state
      delete container.dataset.batteryVariantId;
      delete container.dataset.batteryPrice;
      delete container.dataset.batteryLabel;

      container.dispatchEvent(new CustomEvent('battery:change', {
        bubbles: true,
        detail: { tier: 'standard', variantId: null, price: null, label: null }
      }));
    } else {
      const variantId = card.dataset.addonVariantId || null;
      const price = card.dataset.addonPrice || null;
      const label = card.dataset.addonLabel || null;

      container.dataset.batteryVariantId = variantId;
      container.dataset.batteryPrice = price;
      container.dataset.batteryLabel = label;

      container.dispatchEvent(new CustomEvent('battery:change', {
        bubbles: true,
        detail: {
          tier: card.dataset.batteryTier || 'upgrade',
          variantId,
          price: price ? parseInt(price, 10) : null,
          label
        }
      }));
    }
  }

  // ===== Click handler =====
  cards.forEach((card) => {
    card.addEventListener('click', () => selectCard(card));
  });

  // ===== Keyboard: Enter/Space =====
  cards.forEach((card) => {
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        selectCard(card);
      }
    });
  });

  // ===== Initial ARIA state =====
  // Card 1 (standard) starts active via battery-card--active in Liquid.
  // Sync ARIA to match the Liquid-rendered initial state.
  cards.forEach((card) => {
    const active = card.classList.contains('battery-card--active');
    card.setAttribute('role', 'radio');
    card.setAttribute('aria-checked', active ? 'true' : 'false');
    card.setAttribute('tabindex', active ? '0' : '-1');
  });
  container.setAttribute('role', 'radiogroup');
  container.setAttribute('aria-label', 'Selectează baterie');
})();
